'use client';

import { useEffect, useState } from 'react';

import { Button, Typography } from 'lighthouse';

import ProductSortBar, { SortBar } from '@/widgets/ProductSortBar';
import ScrollFade from '@/widgets/ScrollFade';
import SwipeableDrawer from '@/widgets/SwipeableDrawer';

import { ATTRIBUTE_FILTER_COPY } from '@/utilities/attributeFilters/constants';

import { AttributeCategoryNav } from './_components/AttributeCategoryNav';
import { AttributeFiltersPanel } from './_components/AttributeFiltersPanel';
import { AttributeFiltersProps } from './models';

import { Filter } from 'iconsax-react';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Layout per docs:
 * - Desktop: category toolbar → sidebar filters box (active chips inside) | product list
 * - Mobile: toolbar → filter+sort in one bar → list; filters (+ active chips) in drawer with Apply
 */
const AttributeFilters = ({
    schema,
    isLoading,
    selection,
    draftSelection,
    activeCount,
    draftActiveCount,
    hasInvalidDraftRanges,
    onSetValues,
    onSetRange,
    onClearAll,
    onResetDraft,
    onApplyDraft,
    toolbar,
    renderPanel,
    brandFilter,
    categoryNav,
    sortControl,
    sortBy,
    onSortChange,
    children,
    className = ''
}: AttributeFiltersProps) => {
    const [isDesktop, setIsDesktop] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const hasCategoryNav = Boolean(categoryNav?.items.length || categoryNav?.breadcrumb?.length);
    const hasAttributeFilters = isLoading || schema.length > 0 || Boolean(brandFilter) || Boolean(renderPanel);
    const hasFilters = hasAttributeFilters || hasCategoryNav;
    const hasSortBar = Boolean(sortControl || (sortBy && onSortChange));

    const categoryBox = hasCategoryNav && categoryNav ? <AttributeCategoryNav {...categoryNav} /> : null;

    useEffect(() => {
        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const updateViewport = () => setIsDesktop(mediaQuery.matches);

        updateViewport();
        mediaQuery.addEventListener('change', updateViewport);

        return () => mediaQuery.removeEventListener('change', updateViewport);
    }, []);

    const openDrawer = () => {
        onResetDraft();
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        onResetDraft();
    };

    const handleApply = () => {
        if (onApplyDraft()) {
            setIsDrawerOpen(false);
        }
    };

    const mobileFilterLeading = hasAttributeFilters ? (
        <div className='flex items-center'>
            <Button variant='text' color='primary' onClick={openDrawer} className='min-w-0 px-2 py-1'>
                <span className='flex items-center gap-1.5'>
                    <Filter size={18} color='currentColor' variant='Bold' className='shrink-0' />
                    <Typography variant='body2' component='span' className='whitespace-nowrap font-semibold'>
                        {ATTRIBUTE_FILTER_COPY.mobileButton}
                    </Typography>
                    {activeCount > 0 ? (
                        <span className='shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-white'>
                            {activeCount}
                        </span>
                    ) : null}
                </span>
            </Button>
            {activeCount > 0 ? (
                <Button variant='text' color='primary' onClick={() => onClearAll()} className='min-w-0 px-1.5 py-1'>
                    <Typography variant='caption' component='span' className='whitespace-nowrap font-semibold'>
                        {ATTRIBUTE_FILTER_COPY.clearAll}
                    </Typography>
                </Button>
            ) : null}
        </div>
    ) : null;

    const sortBar = sortControl ? (
        <SortBar {...sortControl} leading={mobileFilterLeading ?? undefined} />
    ) : hasSortBar && sortBy && onSortChange ? (
        <ProductSortBar value={sortBy} onChange={onSortChange} leading={mobileFilterLeading ?? undefined} />
    ) : null;

    if (!hasFilters) {
        return (
            <div className={`space-y-3 ${className}`.trim()}>
                {toolbar}
                {categoryBox}
                {sortBar}
                {children}
            </div>
        );
    }

    return (
        <div className={`space-y-3 ${className}`.trim()}>
            {toolbar}
            {categoryBox}

            <div className={`grid grid-cols-1 items-start gap-4 ${hasAttributeFilters ? 'md:grid-cols-12' : ''}`.trim()}>
                {hasAttributeFilters ? (
                    <aside className='hidden self-stretch md:col-span-3 md:block'>
                        <ScrollFade
                            className='sticky top-20 z-10'
                            scrollerClassName='nice-scroll max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain pe-1'>
                            {renderPanel ? (
                                renderPanel(false)
                            ) : (
                                <AttributeFiltersPanel
                                    schema={schema}
                                    brandFilter={brandFilter}
                                    selection={selection}
                                    isLoading={isLoading}
                                    onSetValues={onSetValues}
                                    onSetRange={onSetRange}
                                    onClearAll={onClearAll}
                                />
                            )}
                        </ScrollFade>
                    </aside>
                ) : null}

                <div className={hasAttributeFilters ? 'md:col-span-9' : ''}>
                    {hasAttributeFilters && !hasSortBar ? (
                        <div className='mb-3 flex items-stretch overflow-hidden rounded-lg border border-solid border-primary md:hidden'>
                            <Button
                                variant='text'
                                color='primary'
                                onClick={openDrawer}
                                className='flex min-w-0 flex-1 items-center justify-start gap-2 rounded-none px-3 py-2'>
                                <span className='flex min-w-0 flex-nowrap items-center gap-2'>
                                    <Filter size={18} color='currentColor' variant='Bold' className='shrink-0' />
                                    <Typography variant='body2' component='span' className='whitespace-nowrap font-semibold'>
                                        {ATTRIBUTE_FILTER_COPY.mobileButton}
                                    </Typography>
                                    {activeCount > 0 && (
                                        <span className='shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-bold leading-none text-white'>
                                            {activeCount}
                                        </span>
                                    )}
                                </span>
                            </Button>
                            {activeCount > 0 && (
                                <>
                                    <span className='w-px shrink-0 self-stretch bg-primary/30' aria-hidden />
                                    <Button variant='text' color='primary' onClick={() => onClearAll()} className='shrink-0 rounded-none px-3 py-2'>
                                        <Typography variant='body2' component='span' className='whitespace-nowrap font-semibold'>
                                            {ATTRIBUTE_FILTER_COPY.clearAll}
                                        </Typography>
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : null}

                    {sortBar ? <div className='mb-3'>{sortBar}</div> : null}
                    {children}
                </div>
            </div>

            {isDrawerOpen && !isDesktop && hasAttributeFilters && (
                <SwipeableDrawer open title={ATTRIBUTE_FILTER_COPY.panelTitle} onClose={closeDrawer} PaperProps={{ className: 'max-h-[85vh]' }}>
                    <div className='flex min-h-full flex-col'>
                        <div className='flex-1 px-4 pb-4 pt-2'>
                            {renderPanel ? (
                                renderPanel(true)
                            ) : (
                                <AttributeFiltersPanel
                                    schema={schema}
                                    brandFilter={brandFilter}
                                    selection={draftSelection}
                                    isLoading={isLoading}
                                    draft
                                    hasInvalidRanges={hasInvalidDraftRanges}
                                    onSetValues={onSetValues}
                                    onSetRange={onSetRange}
                                    onClearAll={onClearAll}
                                />
                            )}
                        </div>
                        <div className='sticky bottom-0 z-10 flex gap-2 border-t border-solid border-N100 bg-white px-4 py-3'>
                            <Button
                                variant='outlined'
                                color='blackMediumEmphasis'
                                className='flex-1'
                                onClick={() => {
                                    onClearAll({ draft: true });
                                }}>
                                {ATTRIBUTE_FILTER_COPY.clearAll}
                            </Button>
                            <Button variant='contained' color='primary' className='flex-1' onClick={handleApply} disabled={hasInvalidDraftRanges}>
                                {ATTRIBUTE_FILTER_COPY.apply}
                                {draftActiveCount > 0 ? ` (${draftActiveCount})` : ''}
                            </Button>
                        </div>
                    </div>
                </SwipeableDrawer>
            )}
        </div>
    );
};

export default AttributeFilters;
